import * as Sentry from '@sentry/react'
import axios, { type AxiosRequestConfig } from 'axios'
import { getRuntimeConfig, type ServiceName } from './runtime-config'
import { toApiError } from './api-error'
import { getLocalDevelopmentAccessToken } from '@/config/local-development-auth'
import { profileApiPath } from '@/config/profile-api'
import { isDemoDataRoute } from '@/demo/microservice/config'
import { expireSessionAndRedirectToLogin } from '@/features/session/services/session-expiration'
import { isForgeSphereApiUrl } from './forgesphere-fetch'
declare module 'axios' {
  interface AxiosRequestConfig {
    /**     * For a read that only decorates a page: a 401 on it is shown as an error, but does not end the session and send the     * person to the login page. The calls the page really depends on still do.     */ skipSessionExpiry?: boolean
  }
}

const enabledApiPaths = new Set([profileApiPath])

const cicdConfigurationPath = /^\/v1\/api\/cicd-config\/[^/]+\/all$/
const cicdConfigDraftPath = /^\/v1\/api\/cicd-config\/[^/]+\/draft$/
const cicdConfigPipelinePath = /^\/v1\/api\/cicd-config\/[^/]+\/[^/]+$/
const cicdConfigStrategyPath =
  /^\/v1\/api\/cicd-config\/[^/]+\/strategies(?:\/[^/]+(?:\/default)?)?$/
const cicdConfigApplyProfilePath =
  /^\/v1\/api\/cicd-config\/[^/]+\/apply-profile\/[^/]+$/
const cicdProfilePath = /^\/v1\/api\/profiles(?:\/.*)?$/
const cicdStrategyPath = /^\/v1\/api\/branching-strategies(?:\/[^/]+)?$/
const cicdTestPath =
  /^\/v1\/api\/test\/(?:apigee|kong(?:\/regions)?|scm\/teams)$/
const v2CicdPath = /^\/v2\/cicd(?:\/|$)/
const v2CicdAuthorizationPath = /^\/v2\/api\/authorization\/cicd(?:\/|$)/
const v2FeatureAuthorizationPath =
  /^\/v2\/api\/authorization\/features\/(?:access|decision)$/
const codeReviewPath = /^\/v1\/api\/code-reviews(?:\/|$)/
const databaseConnectionTestPath = '/api/v1/database/test-connection'
const forgeSphereLogSearchPath = '/v1/api/forgesphere-logs/search'
const adhocConnectionTestPath = '/adhoc/execute'
const microserviceMonitoringPath =
  '/v2/api/api-development/monitoring/prometheus'
const v2ResourcePath = '/v2/api/resources'
const v2ResourceItemPath = /^\/v2\/api\/resources\/[^/]+$/
const v2ResourceReadPath =
  /^\/v2\/api\/resources\/[^/]+\/(onboarding|connector|connector\/readiness|steps|activities)$/
const v2ResourceAccessPath = /^\/v2\/api\/resources\/[^/]+\/access$/
const v2ResourceGrantPath = /^\/v2\/api\/resources\/[^/]+\/access\/grants$/
const v2ResourceGrantRevokePath =
  /^\/v2\/api\/resources\/[^/]+\/access\/grants\/[^/]+\/revoke$/
const v2ResourceOwnerTransferPath =
  /^\/v2\/api\/resources\/[^/]+\/access\/owner-transfer$/
const v2ResourceCommandPath =
  /^\/v2\/api\/resources\/[^/]+\/(archive|restore|onboarding\/complete)$/
const v2StepStatusPath =
  /^\/v2\/api\/resources\/[^/]+\/steps\/[^/]+\/(?:status|advance)$/
const v2WorkflowCatalogPath = /^\/v2\/api\/workflow-catalog(?:\/[^/]+)?$/
const v2WorkflowTemplatesPath =
  /^\/v2\/api\/workflow-templates(?:\/[^/]+(?:\/(?:latest|versions(?:\/[^/]+)?))?)?$/
const v2WorkflowAssignmentPath =
  /^\/v2\/api\/applications\/[^/]+\/workflow-assignments\/[^/]+(?:\/resolved)?$/
const v2WorkflowOverviewPath = '/v2/api/workflow-assignments/overview'
const v2WorkflowMigrationPath =
  /^\/v2\/api\/resources\/[^/]+\/workflow-migration(?:\/(?:preview|history))?$/
const v2ConnectorPath = /^\/v2\/api\/resources\/[^/]+\/connector$/
const v2PlatformCollectionPath =
  /^\/v2\/api\/platform-onboarding\/(business-units|projects|applications|consumers)$/
const v2PlatformApplicationPath =
  /^\/v2\/api\/platform-onboarding\/applications\/[^/]+$/
const v2PlatformConsumersPath =
  /^\/v2\/api\/platform-onboarding\/applications\/[^/]+\/consumers$/
const v2RequirementPath = /^\/v2\/api\/resources\/[^/]+\/requirements$/
const v2RequirementCommandPath =
  /^\/v2\/api\/resources\/[^/]+\/requirements\/(complete|skip|spec-recommendations)$/
const v2ApiDesignResourcePath =
  /^\/v2\/api\/resources\/[^/]+\/api-design(?:\/specifications|\/generated-specification|\/library-clone|\/library-updates(?:\/stay)?|\/library-upgrade)?$/
const v2ValidationRunsPath =
  /^\/v2\/api\/resources\/[^/]+\/api-design\/validation-runs$/
const v2SpecificationPath =
  /^\/v2\/api\/specifications(?:\/library|\/[^/]+(?:\/content|\/promote|\/endpoints)?)$/
const v2SchemaPath = /^\/v2\/api\/schemas(?:\/.*)?$/
const v2LibraryPath = /^\/v2\/api\/library(?:\/|$)/
const v2ApiDevelopmentPath =
  /^\/v2\/api\/resources\/[^/]+\/api-development(?:\/artifact\/(?:download|download-link)|\/operations(?:\/latest|\/[^/]+(?:\/events|\/cancel)?)?)?$/
const webIdeWorkspacePath =
  /^\/v1\/workspaces\/[a-f\d]{24}\/(?:open|files|file|search|terminal-sessions(?:\/[a-f\d-]{36})?)$/i
const v2DeploymentPath =
  /^\/v2\/api\/resources\/[^/]+\/deployments(?:\/artifacts(?:\/sync)?|\/nexus-versions|\/history|\/(?:promote|rollback)(?:\/github)?|\/[^/]+\/github\/status)$/
const v2ResourceCopyPath =
  /^\/v2\/api\/resources\/[^/]+\/copy-operations(?:\/latest|\/[^/]+(?:\/events)?)?$/
const migrationConnectorViewsPath =
  /^\/v1\/api\/connector-configurations\/organization\/[^/]+\/migration-views$/
const apigeeWrapperPath =
  /^\/(?:auth\/apigee\/token|organizations(?:\/.*)?|profiles(?:\/test-connection)?)$/
const kongWrapperPath =
  /^\/(?:config\/konnect\/(?:profiles|verify)|v2\/control-planes(?:\/.*)?|analytics\/(?:dashboard|requests(?:\/page)?)|tracing(?:\/.*)?)$/

export const httpClient = axios.create({
  timeout: 30_000,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
  },
})

const CORRELATION_APPLICATION_NAME = 'forgesphere-lifecycle-fe'
const CORRELATION_APPLICATION_HASH = hashText(CORRELATION_APPLICATION_NAME)

httpClient.interceptors.request.use((request) => {
  const developmentAccessToken = getLocalDevelopmentAccessToken()
  const profileRequest = isProfileApiRequest(request.url)
  const apigeeTokenRequest =
    getRequestPath(request.url ?? '') === '/auth/apigee/token'
  request.headers.set('X-Correlation-Id', createCorrelationId())
  request.headers.set('X-Partner-Id', getPartnerId())
  if (
    developmentAccessToken &&
    !profileRequest &&
    !apigeeTokenRequest &&
    !request.headers.has('Authorization')
  ) {
    request.headers.set('Authorization', `Bearer ${developmentAccessToken}`)
  } else if (profileRequest || apigeeTokenRequest) {
    request.headers.delete('Authorization')
  }
  if (
    isDemoDataRoute() &&
    !isEnabledApiRequest(request.url, request.method) &&
    typeof request.adapter !== 'function'
  ) {
    request.adapter = async (config) => ({
      data: {},
      status: 200,
      statusText: 'Local preview',
      headers: {},
      config,
    })
  }
  return request
})

export function isEnabledApiRequest(
  url: string | undefined,
  method: string | undefined,
) {
  if (!url) return false
  const path = getRequestPath(url)
  if (!path) return false
  const requestMethod = (method ?? 'GET').toUpperCase()
  if (apigeeWrapperPath.test(path)) return true
  if (kongWrapperPath.test(path)) return true
  if (v2CicdPath.test(path)) return true
  if (requestMethod === 'GET' && v2CicdAuthorizationPath.test(path)) return true
  if (requestMethod === 'POST' && v2FeatureAuthorizationPath.test(path))
    return true
  if (requestMethod === 'GET' && v2FeatureAuthorizationPath.test(path))
    return true
  if (isCicdAutomationRequest(path, requestMethod)) return true
  if (codeReviewPath.test(path)) return true
  if (serviceRequestMethodIsWorkflow(path, requestMethod)) return true
  if (requestMethod === 'POST' && path === v2WorkflowOverviewPath) return true
  if (v2LibraryPath.test(path)) return true
  if (requestMethod === 'GET' && path === v2ResourcePath) return true
  if (requestMethod === 'GET' && v2ResourceItemPath.test(path)) return true
  if (requestMethod === 'GET' && v2ResourceReadPath.test(path)) return true
  if (requestMethod === 'GET' && v2ResourceAccessPath.test(path)) return true
  if (requestMethod === 'GET' && v2RequirementPath.test(path)) return true
  if (requestMethod === 'GET' && v2ApiDesignResourcePath.test(path)) return true
  if (requestMethod === 'GET' && v2ValidationRunsPath.test(path)) return true
  if (requestMethod === 'GET' && v2SpecificationPath.test(path)) return true
  // Data models (v2 schemas) cover every method under this prefix: browsing, editing, importing and writing them
  // into a specification are all guarded by the shared client's auth, not by method here.
  if (v2SchemaPath.test(path)) return true
  if (webIdeWorkspacePath.test(path)) return true
  if (requestMethod === 'GET' && v2ApiDevelopmentPath.test(path)) return true
  if (requestMethod === 'GET' && v2DeploymentPath.test(path)) return true
  if (requestMethod === 'GET' && v2ResourceCopyPath.test(path)) return true
  if (requestMethod === 'GET' && v2PlatformCollectionPath.test(path))
    return true
  if (requestMethod === 'GET' && v2PlatformApplicationPath.test(path))
    return true
  if (requestMethod === 'GET' && migrationConnectorViewsPath.test(path))
    return true
  if (requestMethod === 'POST' && path === v2ResourcePath) return true
  if (requestMethod === 'POST' && v2ResourceCommandPath.test(path)) return true
  if (requestMethod === 'POST' && v2StepStatusPath.test(path)) return true
  if (requestMethod === 'POST' && v2RequirementCommandPath.test(path))
    return true
  if (requestMethod === 'POST' && v2ApiDesignResourcePath.test(path))
    return true
  if (requestMethod === 'POST' && v2ValidationRunsPath.test(path)) return true
  if (requestMethod === 'POST' && v2SpecificationPath.test(path)) return true
  if (requestMethod === 'POST' && v2ApiDevelopmentPath.test(path)) return true
  if (requestMethod === 'POST' && v2DeploymentPath.test(path)) return true
  if (requestMethod === 'POST' && v2ResourceCopyPath.test(path)) return true
  if (requestMethod === 'POST' && v2ResourceGrantRevokePath.test(path))
    return true
  if (requestMethod === 'POST' && v2ResourceOwnerTransferPath.test(path))
    return true
  if (requestMethod === 'PUT' && v2ResourceCommandPath.test(path)) return true
  if (requestMethod === 'PUT' && v2ConnectorPath.test(path)) return true
  if (requestMethod === 'PUT' && v2RequirementPath.test(path)) return true
  if (requestMethod === 'PUT' && v2ApiDesignResourcePath.test(path)) return true
  if (requestMethod === 'PUT' && v2SpecificationPath.test(path)) return true
  if (requestMethod === 'PUT' && v2ApiDevelopmentPath.test(path)) return true
  if (requestMethod === 'PUT' && v2PlatformConsumersPath.test(path)) return true
  if (requestMethod === 'PUT' && v2ResourceGrantPath.test(path)) return true
  if (requestMethod === 'PATCH' && v2ResourceItemPath.test(path)) return true
  if (requestMethod === 'PATCH' && v2PlatformApplicationPath.test(path))
    return true
  if (requestMethod === 'DELETE' && v2ResourceItemPath.test(path)) return true
  if (requestMethod === 'POST') {
    return (
      path === databaseConnectionTestPath ||
      path === adhocConnectionTestPath ||
      path === microserviceMonitoringPath ||
      path === forgeSphereLogSearchPath
    )
  }
  if (requestMethod !== 'GET') return false
  return (
    [...enabledApiPaths].some((enabledPath) => path.endsWith(enabledPath)) ||
    cicdConfigurationPath.test(path)
  )
}

function serviceRequestMethodIsWorkflow(path: string, method: string) {
  if (v2WorkflowCatalogPath.test(path)) return method === 'GET'
  if (v2WorkflowTemplatesPath.test(path))
    return method === 'GET' || method === 'POST'
  if (v2WorkflowAssignmentPath.test(path))
    return ['GET', 'PUT', 'DELETE'].includes(method)
  if (v2WorkflowMigrationPath.test(path))
    return method === 'GET' || method === 'POST'
  return false
}

function isCicdAutomationRequest(path: string, method: string) {
  if (method === 'GET' && cicdConfigurationPath.test(path)) return true
  if (method === 'POST' && cicdConfigDraftPath.test(path)) return true
  if (method === 'PUT' && cicdConfigPipelinePath.test(path)) return true
  if (
    ['POST', 'PATCH', 'DELETE'].includes(method) &&
    cicdConfigStrategyPath.test(path)
  )
    return true
  if (method === 'POST' && cicdConfigApplyProfilePath.test(path)) return true
  if (
    cicdProfilePath.test(path) &&
    ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].includes(method)
  )
    return true
  if (
    cicdStrategyPath.test(path) &&
    ['GET', 'POST', 'PUT', 'DELETE'].includes(method)
  )
    return true
  return method === 'POST' && cicdTestPath.test(path)
}

export function isProfileApiRequest(url: string | undefined) {
  if (!url) return false
  const path = getRequestPath(url)
  return path.endsWith(profileApiPath)
}

function getRequestPath(url: string) {
  try {
    return new URL(url, 'https://forgesphere.local').pathname.replace(/\/$/, '')
  } catch {
    return url.split('?')[0]?.replace(/\/$/, '') ?? ''
  }
}

export function createCorrelationId() {
  const timestamp = new Date().toISOString().replace(/[-:.]/g, '')
  const requestEntropy = globalThis.crypto.randomUUID().slice(0, 8)
  return `${timestamp}-${CORRELATION_APPLICATION_HASH}-${requestEntropy}`
}

function hashText(value: string) {
  let hash = 0x811c9dc5
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

function getPartnerId() {
  if (typeof window === 'undefined') return 'probestack'
  return (window.localStorage.getItem('companyName') || 'probestack')
    .trim()
    .toLowerCase()
}

export function getAuthenticatedRequestHeaders(): Record<string, string> {
  const developmentAccessToken = getLocalDevelopmentAccessToken()
  return {
    'X-Correlation-Id': createCorrelationId(),
    ...(developmentAccessToken
      ? { Authorization: `Bearer ${developmentAccessToken}` }
      : {}),
    'X-Partner-Id': getPartnerId(),
  }
}

httpClient.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    const apiError = toApiError(error)
    if (
      apiError.status === 401 &&
      axios.isAxiosError(error) &&
      !error.config?.skipSessionExpiry &&
      error.config?.url &&
      !isProfileApiRequest(error.config.url) &&
      isForgeSphereApiUrl(error.config.url, error.config.baseURL)
    ) {
      expireSessionAndRedirectToLogin()
    }
    Sentry.captureException(apiError, {
      tags: {
        errorKind: apiError.kind,
        status: String(apiError.status ?? 'none'),
      },
      extra: {
        correlationId: apiError.correlationId,
      },
    })
    return Promise.reject(apiError)
  },
)

export function serviceRequest<T>(
  service: ServiceName,
  request: AxiosRequestConfig,
) {
  if (
    service === 'workflowOrchestrator' &&
    !getRuntimeConfig().services.workflowOrchestrator
  ) {
    return Promise.reject(
      new Error('Workflow orchestrator URL is not configured'),
    )
  }
  const enabledRequest = isEnabledApiRequest(request.url, request.method)
  const liveRequest =
    enabledRequest &&
    (service === 'workflowOrchestrator' ||
      service === 'webIde' ||
      service === 'apiTest' ||
      service === 'apigee' ||
      service === 'kong' ||
      (!isDemoDataRoute() &&
        (service === 'onboarding' ||
          service === 'cicdAutomation' ||
          service === 'codeReview' ||
          service === 'requirements' ||
          service === 'apiDesign' ||
          service === 'apiDevelopment')))
  const demoRequest = isDemoDataRoute() && !liveRequest

  return httpClient.request<T>({
    ...request,
    baseURL: getRuntimeConfig().services[service] ?? '/mcp-generate',
    ...(demoRequest
      ? {
          adapter: async (config) => {
            const { handleDemoRequest } =
              await import('@/demo/microservice/handler')
            const data = await handleDemoRequest(service, config)
            return { data, status: 200, statusText: 'OK', headers: {}, config }
          },
        }
      : {}),
  })
}
